import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Trash2, RotateCcw, RefreshCw, Copy, Check, Download, KeyRound, Power, PowerOff, Settings2 } from 'lucide-react';
import { vpnWireguardAPI, vpnServersAPI } from '../api';
import { Card, Button, Badge, Table, Pagination, Modal, Input, Select, PageHeader, Alert, useSort } from '../components/UI';
import { useTenant } from '../TenantContext';
import { useAuth } from '../context/AuthContext';

const MenuItem = ({ children, onClick, color, loading }) => (
  <button onClick={onClick} disabled={loading} style={{
    display: 'block', width: '100%', textAlign: 'left',
    padding: '9px 14px', background: 'none', border: 'none',
    cursor: loading ? 'wait' : 'pointer', fontSize: 13,
    color: color || 'var(--text-secondary)', transition: 'background 0.1s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >{children}</button>
);

const DropdownPortal = ({ anchorRef, open, children }) => {
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  React.useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX });
    }
  }, [open, anchorRef]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'absolute', top: pos.top, left: pos.left, transform: 'translateX(-100%)', zIndex: 9999, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', minWidth: 180, overflow: 'hidden' }}
      onClick={e => e.stopPropagation()}>
      {children}
    </div>,
    document.body
  );
};

const SyncBadge = ({ status }) => {
  const map = { synced: { color: 'var(--accent-green)', bg: 'rgba(16,185,129,0.12)' }, pending: { color: 'var(--accent-orange)', bg: 'rgba(245,158,11,0.12)' }, error: { color: 'var(--accent-red)', bg: 'rgba(239,68,68,0.12)' } };
  const s = map[status] || { color: 'var(--text-muted)', bg: 'var(--bg-input)' };
  return <span style={{ fontSize: 11, padding: '2px 8px', background: s.bg, color: s.color, borderRadius: 20, border: `1px solid ${s.color}44` }}>{status || '—'}</span>;
};

const CopyBtn = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'inline-flex', verticalAlign: -2 }}>
      {copied ? <Check size={12} color="var(--accent-green)" /> : <Copy size={12} />}
    </button>
  );
};

const downloadFile = (filename, content) => {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const EMPTY_FORM = { name: '', vpn_server_id: '', ip_address: '', dns: '', allowed_ips: '', keepalive: 25, psk: true, status: 'active' };

export default function VpnWireguard() {
  const { selectedTenant } = useTenant();
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin';

  const [servers, setServers] = useState([]);
  const [peers, setPeers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const [serverFilter, setServerFilter] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [availableIps, setAvailableIps] = useState([]);

  const [createdPeer, setCreatedPeer] = useState(null); // hasil create/regen — sekali tampil private_key

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', dns: '', allowed_ips: '', keepalive: '', ip_address: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [regenConfirm, setRegenConfirm] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePermanent, setDeletePermanent] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeletePermanent, setBulkDeletePermanent] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);

  const { sort, onSortChange, sortParam } = useSort();

  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  useEffect(() => {
    vpnServersAPI.list({ type: 'wireguard', status: 'active' }).then(r => setServers(r.vpn_servers || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!selectedTenant) { setPeers([]); return; }
    setLoading(true); setError(null);
    try {
      const params = { page, per_page: perPage };
      if (onlyDeleted) params.only_deleted = true;
      if (serverFilter) params.vpn_server_id = serverFilter;
      if (q) params.q = q;
      if (sortParam) params.sort = sortParam;
      const res = await vpnWireguardAPI.list(selectedTenant, params);
      setPeers(res.vpn_wireguard_peers || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [selectedTenant, page, perPage, onlyDeleted, serverFilter, q, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected([]); }, [selectedTenant, onlyDeleted, serverFilter]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openCreate = () => {
    if (!selectedTenant) { setError('Pilih tenant terlebih dahulu.'); return; }
    if (servers.length === 0) { setError('Belum ada VPN server (wireguard) yang terdaftar & aktif.'); return; }
    setForm({ ...EMPTY_FORM, vpn_server_id: servers[0].id });
    setAvailableIps([]); setFormError(null); setModalOpen(true);
  };

  useEffect(() => {
    if (!modalOpen || !form.vpn_server_id) return;
    vpnWireguardAPI.availableIps(selectedTenant, form.vpn_server_id)
      .then(r => setAvailableIps(r.available || []))
      .catch(() => setAvailableIps([]));
  }, [modalOpen, form.vpn_server_id, selectedTenant]);

  const handleSave = async () => {
    setSaving(true); setFormError(null);
    try {
      const payload = { ...form };
      if (!payload.ip_address) delete payload.ip_address;
      if (!payload.dns) delete payload.dns;
      if (!payload.allowed_ips) delete payload.allowed_ips;
      const res = await vpnWireguardAPI.create(selectedTenant, payload);
      setModalOpen(false); setCreatedPeer(res); setSuccess('Peer ditambahkan.'); load();
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || deletePermanent);
    try {
      if (permanent) await vpnWireguardAPI.deletePermanent(selectedTenant, deleteConfirm.id);
      else await vpnWireguardAPI.delete(selectedTenant, deleteConfirm.id);
      setDeleteConfirm(null); setDeletePermanent(false);
      setSuccess(permanent ? 'Peer dihapus permanen.' : 'Peer dihapus.'); load();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  };

  const handleRestore = async (p) => {
    try {
      const res = await vpnWireguardAPI.restore(selectedTenant, p.id);
      setSuccess('Peer dipulihkan (keypair BARU digenerate, download config ulang).');
      setCreatedPeer(res); load();
    } catch (e) { setError(e.message); }
  };

  const handleSync = async (p) => {
    setSyncingId(p.id);
    try { await vpnWireguardAPI.sync(selectedTenant, p.id); setSuccess('Peer di-sync.'); load(); }
    catch (e) { setError(e.message); } finally { setSyncingId(null); }
  };

  const handleViewConfig = async (p) => {
    try { const res = await vpnWireguardAPI.get(selectedTenant, p.id); setCreatedPeer(res); }
    catch (e) { setError(e.message); }
  };

  const handleToggleStatus = async (p) => {
    try {
      await vpnWireguardAPI.update(selectedTenant, p.id, { status: p.status === 'active' ? 'inactive' : 'active' });
      setSuccess(p.status === 'active' ? 'Peer di-disable.' : 'Peer di-enable.'); load();
    } catch (e) { setError(e.message); }
  };

  const openEdit = (p) => {
    setEditTarget(p);
    setEditForm({ name: p.name || '', dns: p.dns || '', allowed_ips: p.allowed_ips || '', keepalive: p.keepalive ?? '', ip_address: p.ip_address || '' });
    setEditError(null); setRegenConfirm(false);
  };

  const handleEditSave = async () => {
    setEditSaving(true); setEditError(null);
    try {
      const payload = {};
      if (editForm.name !== editTarget.name) payload.name = editForm.name;
      if (editForm.dns !== (editTarget.dns || '')) payload.dns = editForm.dns;
      if (editForm.allowed_ips !== (editTarget.allowed_ips || '')) payload.allowed_ips = editForm.allowed_ips;
      if (String(editForm.keepalive) !== String(editTarget.keepalive ?? '')) payload.keepalive = editForm.keepalive === '' ? null : +editForm.keepalive;
      if (editForm.ip_address !== editTarget.ip_address) payload.ip_address = editForm.ip_address;
      if (Object.keys(payload).length === 0) { setEditTarget(null); return; }
      const res = await vpnWireguardAPI.update(selectedTenant, editTarget.id, payload);
      setEditTarget(null); setSuccess('Peer diperbarui.');
      if (payload.ip_address) setCreatedPeer(res);
      load();
    } catch (e) { setEditError(e.message); }
    finally { setEditSaving(false); }
  };

  const handleRegenerate = async () => {
    setEditSaving(true); setEditError(null);
    try {
      const res = await vpnWireguardAPI.update(selectedTenant, editTarget.id, { regenerate_key: true });
      setEditTarget(null); setRegenConfirm(false);
      setSuccess('Keypair baru digenerate. Client WAJIB download config baru.');
      setCreatedPeer(res); load();
    } catch (e) { setEditError(e.message); }
    finally { setEditSaving(false); }
  };

  const handleBulkDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || bulkDeletePermanent);
    try {
      if (permanent) await Promise.all(selected.map(id => vpnWireguardAPI.deletePermanent(selectedTenant, id)));
      else await Promise.all(selected.map(id => vpnWireguardAPI.delete(selectedTenant, id)));
      setSuccess(`${selected.length} peer ${permanent ? 'dihapus permanen' : 'dihapus'}.`);
      setSelected([]); setBulkDeleteConfirm(false); setBulkDeletePermanent(false); load();
    } catch (e) { setError(e.message); setBulkDeleteConfirm(false); }
  };

  const handleBulkRestore = async () => {
    try {
      await Promise.all(selected.map(id => vpnWireguardAPI.restore(selectedTenant, id)));
      setSuccess(`${selected.length} peer dipulihkan (keypair baru digenerate).`); setSelected([]); load();
    } catch (e) { setError(e.message); }
  };

  const handleBulkSync = async () => {
    setBulkSyncing(true);
    try {
      await Promise.all(selected.map(id => vpnWireguardAPI.sync(selectedTenant, id)));
      setSuccess(`${selected.length} peer di-sync.`); load();
    } catch (e) { setError(e.message); }
    finally { setBulkSyncing(false); }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true, render: v => <span style={{ fontSize: 13 }}>{v}</span> },
    { key: 'vpn_server_name', label: 'Server', render: v => <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v || '—'}</span> },
    { key: 'ip_address', label: 'IP Tunnel', sortable: true, render: v => <span className="mono" style={{ fontSize: 12 }}>{v}</span> },
    { key: 'public_key', label: 'Public Key', render: v => <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v ? `${v.slice(0, 12)}…` : '—'} <CopyBtn value={v} /></span> },
    { key: 'status', label: 'Status', sortable: true, render: v => <Badge status={v} /> },
    { key: 'sync_status', label: 'Sync', sortable: true, render: v => <SyncBadge status={v} /> },
    {
      key: 'id', label: '', render: (_, p) => {
        const btnRef = React.createRef();
        return (
          <div>
            <button ref={btnRef} onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === p.id ? null : p.id); }}
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1 }}>···</button>
            <DropdownPortal anchorRef={btnRef} open={openMenuId === p.id}>
              {onlyDeleted ? (
                <MenuItem color="var(--accent-green)" onClick={() => { handleRestore(p); setOpenMenuId(null); }}><RotateCcw size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Restore</MenuItem>
              ) : (<>
                <MenuItem onClick={() => { handleViewConfig(p); setOpenMenuId(null); }}><KeyRound size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Lihat Config</MenuItem>
                <MenuItem onClick={() => { openEdit(p); setOpenMenuId(null); }}><Settings2 size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Edit / Regenerate</MenuItem>
                <MenuItem onClick={() => { handleToggleStatus(p); setOpenMenuId(null); }}>
                  {p.status === 'active' ? <PowerOff size={13} style={{ marginRight: 6, verticalAlign: -2 }} /> : <Power size={13} style={{ marginRight: 6, verticalAlign: -2 }} />}
                  {p.status === 'active' ? 'Disable' : 'Enable'}
                </MenuItem>
                <MenuItem loading={syncingId === p.id} onClick={() => { handleSync(p); setOpenMenuId(null); }}><RefreshCw size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Sync</MenuItem>
                <div style={{ borderTop: '1px solid var(--border)' }} />
                <MenuItem color="var(--accent-red)" onClick={() => { setDeleteConfirm(p); setDeletePermanent(false); setOpenMenuId(null); }}><Trash2 size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Hapus</MenuItem>
              </>)}
            </DropdownPortal>
          </div>
        );
      }
    },
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title="VPN — WireGuard Peer"
        subtitle={`${total} peer${onlyDeleted ? ' (terhapus)' : ''}`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {selected.length > 0 && (<>
              {!onlyDeleted && <Button variant="ghost" loading={bulkSyncing} onClick={handleBulkSync}><RefreshCw size={13} /> Sync {selected.length}</Button>}
              {onlyDeleted && <Button variant="success" onClick={handleBulkRestore}><RotateCcw size={13} /> Restore {selected.length}</Button>}
              {(!onlyDeleted || isSuperadmin) && (
                <Button variant="danger" onClick={() => setBulkDeleteConfirm(true)}><Trash2 size={13} /> Hapus {selected.length}</Button>
              )}
            </>)}
            <Button variant={onlyDeleted ? 'secondary' : 'ghost'} onClick={() => setOnlyDeleted(d => !d)}>
              {onlyDeleted ? 'Tampil Aktif' : '🗑 Terhapus'}
            </Button>
            <Button onClick={openCreate} disabled={!selectedTenant}><Plus size={14} /> Tambah Peer</Button>
          </div>
        }
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Cari nama peer…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 14px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', width: 220 }} />
        <select value={serverFilter} onChange={e => setServerFilter(e.target.value)}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Server</option>
          {servers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <Card style={{ padding: 0 }}>
        <Table columns={columns} data={peers} loading={loading} sort={sort} onSortChange={onSortChange}
          emptyMessage={selectedTenant ? (onlyDeleted ? 'Tidak ada data terhapus' : 'Belum ada peer WireGuard') : 'Pilih tenant terlebih dahulu'}
          selected={selected} onSelect={setSelected} />
        <div style={{ padding: '0 16px' }}><Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); }} /></div>
      </Card>

      {/* Create Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Tambah Peer WireGuard" width={460}>
        {formError && <Alert type="error" message={formError} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Nama *" value={form.name} onChange={e => f('name', e.target.value)} placeholder="laptop-budi" />
          <Select label="VPN Server *" value={form.vpn_server_id} onChange={e => f('vpn_server_id', +e.target.value)}>
            {servers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.pool})</option>)}
          </Select>
          <Select label="IP Address" value={form.ip_address} onChange={e => f('ip_address', e.target.value)}>
            <option value="">— Auto-assign —</option>
            {availableIps.map(ip => <option key={ip} value={ip}>{ip}</option>)}
          </Select>
          <Input label="DNS" value={form.dns} onChange={e => f('dns', e.target.value)} placeholder="kosong = pakai default server" />
          <Input label="Allowed IPs (routes client)" value={form.allowed_ips} onChange={e => f('allowed_ips', e.target.value)} placeholder="kosong = pakai default server" />
          <Input label="Keepalive (detik)" type="number" value={form.keepalive} onChange={e => f('keepalive', +e.target.value)} />
          <Select label="Status" value={form.status} onChange={e => f('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px 0' }}>
            <input type="checkbox" checked={form.psk} onChange={e => f('psk', e.target.checked)} style={{ accentColor: 'var(--accent-blue)' }} />
            <span>Pakai Preshared Key (rekomendasi)</span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Batal</Button>
          <Button onClick={handleSave} loading={saving}>Tambah Peer</Button>
        </div>
      </Modal>

      {/* Edit / Regenerate Modal */}
      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title={`Edit Peer — ${editTarget?.name || ''}`} width={460}>
        {editError && <Alert type="error" message={editError} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Nama" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
          <Input label="IP Address" value={editForm.ip_address} onChange={e => setEditForm(p => ({ ...p, ip_address: e.target.value }))} />
          <Input label="DNS" value={editForm.dns} onChange={e => setEditForm(p => ({ ...p, dns: e.target.value }))} />
          <Input label="Allowed IPs" value={editForm.allowed_ips} onChange={e => setEditForm(p => ({ ...p, allowed_ips: e.target.value }))} />
          <Input label="Keepalive (detik)" type="number" value={editForm.keepalive} onChange={e => setEditForm(p => ({ ...p, keepalive: e.target.value }))} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setEditTarget(null)}>Batal</Button>
          <Button onClick={handleEditSave} loading={editSaving}>Simpan</Button>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', marginTop: 20, paddingTop: 16 }}>
          {!regenConfirm ? (
            <Button variant="danger" size="sm" onClick={() => setRegenConfirm(true)}><KeyRound size={13} /> Regenerate Keypair</Button>
          ) : (
            <div>
              <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 10 }}>
                ⚠️ Keypair lama LANGSUNG tidak valid — client wajib download config baru setelah ini.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" size="sm" onClick={() => setRegenConfirm(false)}>Batal</Button>
                <Button variant="danger" size="sm" onClick={handleRegenerate} loading={editSaving}>Ya, Regenerate</Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Config reveal (create / regenerate / restore / view) */}
      <Modal open={!!createdPeer} onClose={() => setCreatedPeer(null)} title={`Config Peer — ${createdPeer?.name || ''}`} width={560}>
        {createdPeer && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {createdPeer.private_key && (
              <p style={{ fontSize: 12, color: 'var(--accent-orange)' }}>
                ⚠️ Private key cuma ditampilkan sekali. Simpan/download sekarang.
              </p>
            )}
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Config file (.conf) <CopyBtn value={createdPeer.config} /></div>
              <pre style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{createdPeer.config}</pre>
            </div>
            {createdPeer.config_routeros && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Script Mikrotik RouterOS <CopyBtn value={createdPeer.config_routeros} /></div>
                <pre style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{createdPeer.config_routeros}</pre>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {createdPeer.config && <Button variant="secondary" onClick={() => downloadFile(`${createdPeer.name}.conf`, createdPeer.config)}><Download size={13} /> Download .conf</Button>}
              <Button onClick={() => setCreatedPeer(null)}>Selesai</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Hapus Peer" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus peer <strong style={{ color: 'var(--accent-red)' }}>{deleteConfirm?.name}</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ Peer ini sudah berada di daftar terhapus, sehingga akan dihapus permanen.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={deletePermanent} onChange={e => setDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleDelete}>Hapus</Button>
        </div>
      </Modal>

      {/* Bulk Delete */}
      <Modal open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)} title="Hapus Bulk Peer" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Hapus <strong style={{ color: 'var(--accent-red)' }}>{selected.length} peer</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ Peer ini sudah berada di daftar terhapus, sehingga akan dihapus permanen.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={bulkDeletePermanent} onChange={e => setBulkDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setBulkDeleteConfirm(false)}>Batal</Button>
          <Button variant="danger" onClick={handleBulkDelete}>Hapus</Button>
        </div>
      </Modal>
    </div>
  );
}
